import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProjectBoosts, getSettings } from '../api/projects';
import { useAuth } from '../context/AuthContext';
import '../styles/Boost.css';
import '../styles/ICOListing.css';
import Navbar from '../components/Navbar';

const WALLET_ADDRESS = '0x9c086800A191E03CcE82d2744BaCE178870a14BB';
const API_URL = 'https://onlineico.com/api';

// Mapping selectedPack → id_setting
const PACK_TO_SETTING = {
    'boost_single':  1,
    'boost_pack10':  2,
    'banner_week':   3,
    'banner_month':  4,
    'premium_week':  5,
    'premium_month': 6,
    'card_image':    7,
};

export default function Boost() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [project, setProject]       = useState(null);
    const [settings, setSettings]     = useState(null);
    const [loading, setLoading]       = useState(true);
    const [section, setSection]       = useState('boost');
    const [selectedPack, setSelectedPack] = useState(null);
    const [txHash, setTxHash]         = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted]   = useState(false);
    const [error, setError]           = useState('');
    const [copied, setCopied]         = useState(false);

    useEffect(() => {
        Promise.all([getProjectBoosts(slug), getSettings()]).then(([data, s]) => {
            setProject(data);
            setSettings(s);
            setLoading(false);
        });
    }, [slug]);

    ////////////////////////////
    // Vérifie si une offre est déjà active
    ////////////////////////////
    const isPromoActive = (type) => {
        if (!project?.active_promos) return false;
        return project.active_promos.some(p => p.promo_type === type && (p.status === 'confirmed' || p.status === 'pending'));
    };

    const isPromoPending = (type) => {
        if (!project?.active_promos) return false;
        return project.active_promos.some(p => p.promo_type === type && p.status === 'pending');
    };

    const getAmount = () => {
        if (!settings || !selectedPack) return 0;
        const map = {
            'boost_single':  settings.boost_single,
            'boost_pack10':  settings.boost_pack10,
            'banner_week':   settings.banner_week,
            'banner_month':  settings.banner_month,
            'premium_week':  settings.premium_week,
            'premium_month': settings.premium_month,
            'card_image':    settings.card_image,
        };
        return map[selectedPack] || 0;
    };

    ////////////////////////////
    // SOUMISSION PAIEMENT
    ////////////////////////////
    const handleSubmit = async () => {
        if (!selectedPack) { setError('Please choose an option.'); return; }
        if (!txHash.trim()) { setError('Please enter your tx hash.'); return; }

        setSubmitting(true); setError('');

        const token = localStorage.getItem('onlineico_token');
        const result = await fetch(`${API_URL}/boosts.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
                project_id: project.id,
                id_setting:  PACK_TO_SETTING[selectedPack],
                tx_hash:     txHash.trim(),
                promo_type:  section,
            })
        }).then(r => r.json());


        if (result.success) setSubmitted(true);
        else setError(result.error || 'Submission error');
        setSubmitting(false);
    };

    ////////////////////////////
    // ANNULATION OFFRE EN ATTENTE
    ////////////////////////////
    const handleCancel = async (boostId) => {
        const token = localStorage.getItem('onlineico_token');
        await fetch(`${API_URL}/cancel-promo.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ boost_id: boostId })
        });
        navigate('/mon-compte');
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(WALLET_ADDRESS);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const colors = ['#34D399', '#8B5CF6', '#F59E0B', '#3B82F6', '#EC4899', '#10B981'];

    if (loading) return <div className="app-wrap"><div style={{ padding: '4rem', textAlign: 'center', color: '#94a3b8' }}>Loading...</div></div>;

    ////////////////////////////
    // PAGE CONFIRMATION
    ////////////////////////////
    if (submitted) return (
        <div className="app-wrap">
            <nav className="nav"><div className="nav-logo" onClick={() => navigate('/')}>ONLINEICO</div></nav>
            <div className="boost-wrap">
                <div className="submit-card">
                    <div className="boost-success">
                        <div className="boost-success-icon">🚀</div>
                        <div className="boost-success-title">Request submitted!</div>
                        <p style={{ color: '#94a3b8', marginBottom: '2rem' }}>
                            Your transaction is being verified.<br />
                            {section === 'banner'     && 'Your banner will be visible after validation by our team.'}
                            {section === 'premium'    && 'Your project will be featured after validation.'}
                            {section === 'boost'      && 'Boosts will be credited after validation.'}
                            {section === 'card_image' && 'Once your payment is validated, go to My Account to upload your banner image.'}
                        </p>
                        <button className="btn-submit" onClick={() => navigate(`/${project.type}/${project.slug}`)}>Back to project</button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="app-wrap">
            <Navbar />

            <div className="boost-wrap">

                {/* HEADER PROJET */}
                {project && (
                    <div className="boost-project-header">
                        <div className="boost-project-logo" style={{ background: colors[project.id % colors.length] }}>
                            {project.name.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                            <div className="boost-project-name">{project.name}</div>
                            <div className="boost-project-stats">
                                <div className="boost-stat">Total boosts: <span>{project.boosts || 0}</span></div>
                                <div className="boost-stat">Last 24h: <span>{project.boosts_24h || 0}</span></div>
                            </div>
                        </div>
                    </div>
                )}

                {/* OFFRES EN ATTENTE DE PAIEMENT */}
                {project?.active_promos?.filter(p => p.status === 'pending').map((promo, i) => (
                    <div key={i} style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '16px', padding: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                        <div>
                            <div style={{ color: '#F59E0B', fontWeight: '600', fontSize: '0.9rem', marginBottom: '4px' }}>
                                Pending payment option: {promo.promo_type}
                            </div>
                            <div style={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                                Proceed with payment below or cancel if you no longer want this option.
                            </div>
                        </div>
                        <button
                            onClick={() => handleCancel(promo.id)}
                            style={{ background: 'none', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#f87171', cursor: 'pointer', fontSize: '0.82rem', padding: '8px 16px', transition: 'all 0.2s' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        >✕ Cancel option</button>
                    </div>
                ))}

                {/* OFFRES ACTIVES */}
                {project?.active_promos?.filter(p => p.status === 'confirmed').map((promo, i) => (
                    <div key={i} style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: '16px', padding: '16px', marginBottom: '16px' }}>
                        <div style={{ color: '#34D399', fontWeight: '600', fontSize: '0.9rem', marginBottom: '4px' }}>
                            ✅ {promo.label} — active until {new Date(promo.promo_until).toLocaleDateString('en-GB')}
                        </div>
                    </div>
                ))}

                {/* ONGLETS */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
                    {[
                        { key: 'boost',      label: '⚡ Boost' },
                        { key: 'banner',     label: '📢 Banner' },
                        { key: 'premium',    label: '⭐ Premium' },
                        { key: 'card_image', label: '🖼️ Card Image' },
                    ].map(s => (
                        <button key={s.key}
                            onClick={() => { setSection(s.key); setSelectedPack(null); setError(''); }}
                            style={{
                                padding: '8px 20px', borderRadius: '30px', border: '1px solid',
                                borderColor: section === s.key ? 'rgba(52,211,153,0.5)' : 'rgba(255,255,255,0.1)',
                                background:  section === s.key ? 'rgba(52,211,153,0.1)' : 'transparent',
                                color:       section === s.key ? '#34D399' : '#94a3b8',
                                cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'Inter, sans-serif',
                                transition: 'all 0.2s'
                            }}
                        >{s.label}</button>
                    ))}
                </div>

                {/* SECTION BOOST */}
                {section === 'boost' && settings && (
                    <>
                        <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '16px', padding: '16px', marginBottom: '24px' }}>
                            <p style={{ color: '#F59E0B', fontSize: '0.85rem', lineHeight: '1.7', margin: 0 }}>
                                ⚡ Give your project a push! A boost temporarily improves your ranking position for 48h. The more you boost, the higher you climb.
                            </p>
                        </div>
                        <div className="boost-packs">
                            {[
                                { key: 'boost_single', qty: 1,  label: '1 Boost',   price: settings.boost_single,  unit: `${settings.boost_single}$ / boost` },
                                { key: 'boost_pack10', qty: 10, label: '10 Boosts',  price: settings.boost_pack10, unit: `${Math.round(settings.boost_pack10 / 10)}$ / boost`, badge: `Économisez ${Math.round((1 - settings.boost_pack10 / (settings.boost_single * 10)) * 100)}%` },
                            ].map(pack => (
                                <div key={pack.key} className={`boost-pack${selectedPack === pack.key ? ' selected' : ''}`} onClick={() => setSelectedPack(pack.key)}>
                                    <div className="boost-pack-qty">{pack.qty}</div>
                                    <div className="boost-pack-label">Boost{pack.qty > 1 ? 's' : ''}</div>
                                    <div className="boost-pack-price">{pack.price} $</div>
                                    <div className="boost-pack-unit">{pack.unit}</div>
                                    {pack.badge && <div className="boost-pack-badge">{pack.badge}</div>}
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {/* SECTION BANNER */}
                {section === 'banner' && settings && (
                    <>
                        <div style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '16px', padding: '16px', marginBottom: '24px' }}>
                            <p style={{ color: '#3B82F6', fontSize: '0.85rem', lineHeight: '1.7', margin: 0 }}>
                                📢 Your advertising banner is displayed in the right column of other projects' detail pages. Ideal for targeting investors already interested in similar projects.
                            </p>
                        </div>
                        {isPromoActive('banner') ? (
                            <div style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: '16px', padding: '16px' }}>
                                <div style={{ color: '#34D399', fontWeight: '600' }}>✅ Your banner is already active.</div>
                            </div>
                        ) : (
                            <div className="boost-packs">
                                {[
                                    { key: 'banner_week',  duration: '1 week', price: settings.banner_week },
                                    { key: 'banner_month', duration: '1 month',    price: settings.banner_month },
                                ].map(pack => (
                                    <div key={pack.key} className={`boost-pack${selectedPack === pack.key ? ' selected' : ''}`} onClick={() => setSelectedPack(pack.key)}>
                                        <div className="boost-pack-qty" style={{ fontSize: '1.2rem' }}>📢</div>
                                        <div className="boost-pack-label">{pack.duration}</div>
                                        <div className="boost-pack-price">{pack.price} $</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* SECTION PREMIUM */}
                {section === 'premium' && settings && (
                    <>
                        <div style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: '16px', padding: '16px', marginBottom: '24px' }}>
                            <p style={{ color: '#8B5CF6', fontSize: '0.85rem', lineHeight: '1.7', margin: 0 }}>
                                ⭐ Your project is displayed as a priority in the "Featured" section on the homepage. Maximum visibility — it's the first thing visitors see when they arrive on the site.
                            </p>
                        </div>
                        {isPromoActive('premium') ? (
                            <div style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: '16px', padding: '16px' }}>
                                <div style={{ color: '#34D399', fontWeight: '600' }}>✅ Your Premium option is already active.</div>
                            </div>
                        ) : (
                            <div className="boost-packs">
                                {[
                                    { key: 'premium_week',  duration: '1 week', price: settings.premium_week },
                                    { key: 'premium_month', duration: '1 month',    price: settings.premium_month },
                                ].map(pack => (
                                    <div key={pack.key} className={`boost-pack${selectedPack === pack.key ? ' selected' : ''}`} onClick={() => setSelectedPack(pack.key)}>
                                        <div className="boost-pack-qty" style={{ fontSize: '1.2rem' }}>⭐</div>
                                        <div className="boost-pack-label">{pack.duration}</div>
                                        <div className="boost-pack-price">{pack.price} $</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* SECTION CARD IMAGE */}
                {section === 'card_image' && settings && (
                    <>
                        <div style={{ background: 'rgba(236,72,153,0.08)', border: '1px solid rgba(236,72,153,0.2)', borderRadius: '16px', padding: '16px', marginBottom: '24px' }}>
                            <p style={{ color: '#EC4899', fontSize: '0.85rem', lineHeight: '1.7', margin: 0 }}>
                                🖼️ Display a banner image directly on your project card in the ICO list. Maximum visibility at first glance — <strong style={{ color: '#F1F5F9' }}>unlimited duration</strong> for <strong style={{ color: '#F1F5F9' }}>{settings.card_image} $</strong>.
                            </p>
                        </div>
                        {isPromoActive('card_image') ? (
                            <div style={{ background: isPromoPending('card_image') ? 'rgba(245,158,11,0.08)' : 'rgba(52,211,153,0.08)', border: `1px solid ${isPromoPending('card_image') ? 'rgba(245,158,11,0.2)' : 'rgba(52,211,153,0.2)'}`, borderRadius: '16px', padding: '16px' }}>
                                {isPromoPending('card_image')
                                    ? <div style={{ color: '#F59E0B', fontWeight: '600' }}>⏳ Your Card Image request has been recorded — pending validation by our team.</div>
                                    : <div style={{ color: '#34D399', fontWeight: '600' }}>✅ Your card image is already active (unlimited).</div>
                                }
                            </div>
                        ) : (
                            <div className="boost-packs" style={{ gridTemplateColumns: '1fr' }}>
                                <div className={`boost-pack${selectedPack === 'card_image' ? ' selected' : ''}`} onClick={() => setSelectedPack('card_image')}>
                                    <div className="boost-pack-qty" style={{ fontSize: '1.5rem' }}>🖼️</div>
                                    <div className="boost-pack-label">Card image — unlimited</div>
                                    <div className="boost-pack-price">{settings.card_image} $</div>
                                    <div className="boost-pack-unit">One-time payment, no subscription</div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* PAIEMENT */}
                {selectedPack && !isPromoActive(section) && (
                    <div className="submit-card" style={{ marginTop: '16px' }}>
                        <div className="submit-card-title"><span>◉</span> Payment</div>
                        <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '16px', lineHeight: '1.7' }}>
                            Send exactly <strong style={{ color: '#F59E0B' }}>{getAmount()} $</strong> in USDT on the <strong style={{ color: '#F1F5F9' }}>BSC (BEP-20)</strong> network to this address:
                        </div>
                        <div className="boost-wallet">
                            <div className="boost-wallet-title">USDT BSC Wallet Address</div>
                            <div className="boost-wallet-address" onClick={handleCopy} title="Click to copy">{WALLET_ADDRESS}</div>
                            <div style={{ textAlign: 'center', color: copied ? '#34D399' : '#94a3b8', fontSize: '0.8rem' }} onClick={handleCopy}>
                                {copied ? '✓ Copied!' : 'Click to copy'}
                            </div>
                            

                        </div>
                        <div className="boost-amount">{getAmount()} $</div>
                        <div className="form-group" style={{ marginTop: '20px' }}>
                            <label className="form-label">Transaction Hash <span style={{ color: '#EC4899' }}>*</span></label>
                            <input className="form-input" placeholder="0x..." value={txHash} onChange={e => setTxHash(e.target.value)} />
                            <div style={{ color: '#64748b', fontSize: '0.78rem', marginTop: '4px' }}>Find your tx hash in your wallet after sending</div>
                        </div>
                        {error && <div style={{ color: '#f87171', fontSize: '0.85rem', margin: '12px 0' }}>{error}</div>}
                        <button className="btn-submit" style={{ marginTop: '16px', width: '100%' }} onClick={handleSubmit} disabled={submitting}>
                            {submitting ? 'Sending...' : '🚀 Submit'}
                        </button>
                    </div>
                )}

            </div>
        </div>
    );
}